<template>
  <div class="flex flex-items flew-wrap gap-2 flex-wrap">
    <gallery-view
      v-if="showGallery"
      :show.sync="showGallery"
      :attachment="activeAttachment"
      :all-attachments="galleryAttachments"
      @close="onGalleryClose"
    />
    <div
      v-for="attachment in attachments"
      :key="attachment.id"
      class="max-w-[25%] min-w-[170px]"
    >
      <div
        v-if="isAttachmentUnavailable(attachment)"
        class="bg-neutral-100 border border-dashed border-gray-300 flex flex-row items-center rounded-[12px] gap-2 px-3 py-[7px]"
      >
        <span
          class="material-symbols-outlined text-[20px] text-gray-400 shrink-0"
        >
          error_outline
        </span>
        <span class="text-xs text-gray-400 truncate">
          {{ $t('CONVERSATION.ATTACHMENT_UNAVAILABLE') }}
        </span>
      </div>
      <div
        v-else
        class="bg-neutral-100 border border-gray-200 flex flex-row items-center rounded-[12px]"
        :class="attachmentClasses"
      >
        <div
          :class="[
            attachmentIconClasses,
            isPreviewable(attachment) && 'cursor-pointer',
          ]"
          @click="isPreviewable(attachment) && openGallery(attachment)"
        >
          <div
            v-if="specialFileType(attachment)"
            :class="specialFileTypeClass(attachment)"
          >
            <template v-if="effectiveFileType(attachment) === 'image'">
              <img
                v-if="!failedThumbnails[attachment.id]"
                :src="attachment.data_url"
                :alt="fileName(attachment.data_url)"
                class="w-full h-full object-cover"
                @error="onThumbnailError(attachment.id)"
              />
              <span
                v-else
                class="material-symbols-outlined text-[24px] text-gray-500"
              >
                image
              </span>
            </template>
            <template v-else-if="effectiveFileType(attachment) === 'video'">
              <video
                v-if="!failedThumbnails[attachment.id]"
                :src="attachment.data_url"
                class="w-full h-full object-cover"
                preload="metadata"
                muted
                playsinline
                @error="onThumbnailError(attachment.id)"
              />
              <span
                v-else
                class="material-symbols-outlined text-[24px] text-gray-500"
              >
                video_file
              </span>
            </template>
            <span
              v-else-if="effectiveFileType(attachment) === 'location'"
              class="material-symbols-outlined text-[24px] text-gray-500"
            >
              location_on
            </span>
            <span
              v-else-if="effectiveFileType(attachment) === 'contact'"
              class="material-symbols-outlined text-[24px] text-gray-500"
            >
              contact_mail
            </span>
          </div>
          <template v-else>
            <img
              :src="documentIcon"
              alt="document-icon"
              class="documentIcon"
              :class="isLarge ? '' : 'ltr:mr-1 rtl:ml-1'"
            />
            <span
              class="flex items-center rounded-[3px] bg-[#d92d20] text-white absolute left-0 select-none uppercase"
              :class="fileExtensitionClasses"
            >
              {{ fileExtensition(attachment.data_url) }}
            </span>
          </template>
        </div>
        <div
          class="flex flex-col justify-between overflow-hidden max-w-[250px]"
          :class="isLarge ? 'h-[42px] leading-4' : ''"
        >
          <span
            v-if="isPreviewable(attachment)"
            class="text-primary text-ellipsis overflow-hidden whitespace-nowrap block cursor-pointer"
            :class="variant === 'large' ? 'text-sm' : 'text-base'"
            @click="openGallery(attachment)"
          >
            {{ fileName(attachment.data_url) }}
          </span>
          <a
            v-else
            class="text-primary text-ellipsis overflow-hidden whitespace-nowrap block"
            :class="variant === 'large' ? 'text-sm' : 'text-base'"
            target="_blank"
            :href="attachment.data_url"
          >
            {{ fileName(attachment.data_url) }}
          </a>
          <div v-if="isLarge" class="text-xs text-secondary">
            {{ formatFileSize(attachment.file_size) }}
          </div>
        </div>
        <a
          v-if="showDownloadIcon"
          :disabled="failedThumbnails[attachment.id]"
          :href="!failedThumbnails[attachment.id] && attachment.data_url"
          target="_blank"
          rel="noreferrer noopener nofollow"
          class="w-11 h-11 flex items-center justify-center button-naked text-gray-500 hover:text-gray-600 hover:bg-gray-200 focus:text-gray-600 focus:bg-gray-200 shrink-0 transition-colors"
        >
          <span class="material-symbols-outlined text-[22px]">download</span>
        </a>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { formatBytes } from 'shared/helpers/FileHelper';
import { MESSAGE_TYPE } from 'shared/constants/messages';
import DocumentIcon from 'dashboard/assets/images/document.svg';
import GalleryView from 'dashboard/components/widgets/conversation/components/GalleryView.vue';

const PREVIEWABLE_FILE_TYPES = ['image', 'video', 'audio'];

const EXTENSION_FILE_TYPES = {
  image: ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp', 'avif', 'heic'],
  video: ['mp4', 'mov', 'webm', 'm4v', '3gp', 'mkv'],
  audio: ['mp3', 'wav', 'ogg', 'oga', 'm4a', 'aac', 'amr', 'opus'],
};

export default {
  components: {
    GalleryView,
  },
  props: {
    attachments: {
      type: Array,
      required: true,
    },
    variant: {
      type: String,
      default: () => 'small',
    },
    showDownloadIcon: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      documentIcon: DocumentIcon,
      failedThumbnails: {},
      showGallery: false,
      activeAttachment: {},
      galleryAttachments: [],
    };
  },
  computed: {
    ...mapGetters({
      currentChatAttachments: 'getSelectedChatAttachments',
    }),
    isLarge() {
      return this.variant === 'large';
    },
    isIncoming() {
      return this.messageType === MESSAGE_TYPE.INCOMING;
    },
    attachmentClasses() {
      if (this.isLarge) return 'gap-3 px-2 py-[7px]';
      return 'gap-1 px-3 py-[7px]';
    },
    attachmentIconClasses() {
      if (this.isLarge)
        return 'w-10 h-10 flex items-center justify-center rounded-lg shrink-0 relative';
      return 'w-6 h-6 flex items-center justify-center rounded-lg shrink-0 relative';
    },
    fileExtensitionClasses() {
      if (this.isLarge)
        return 'text-[9px] py-[2px] px-[4px] h-[15px] font-bold bottom-1.5';
      return 'text-[5px] py-[2px] px-[2px] leading-[5px] font-semibold bottom-1';
    },
  },
  methods: {
    isAttachmentUnavailable(attachment) {
      return !attachment.data_url;
    },
    effectiveFileType(attachment) {
      if (attachment.file_type && attachment.file_type !== 'file') {
        return attachment.file_type;
      }
      const extension = this.fileName(attachment.data_url || '')
        .split('?')[0]
        .split('.')
        .last()
        .toLowerCase();
      const mediaType = Object.keys(EXTENSION_FILE_TYPES).find(type =>
        EXTENSION_FILE_TYPES[type].includes(extension)
      );
      return mediaType || attachment.file_type;
    },
    isPreviewable(attachment) {
      return (
        PREVIEWABLE_FILE_TYPES.includes(this.effectiveFileType(attachment)) &&
        !this.failedThumbnails[attachment.id]
      );
    },
    openGallery(attachment) {
      const normalize = item => ({
        ...item,
        file_type: this.effectiveFileType(item),
      });
      const chatMedia = this.currentChatAttachments
        .map(normalize)
        .filter(item => PREVIEWABLE_FILE_TYPES.includes(item.file_type));
      this.galleryAttachments = chatMedia.some(
        item => item.message_id === attachment.message_id
      )
        ? chatMedia
        : this.attachments
            .map(normalize)
            .filter(item => PREVIEWABLE_FILE_TYPES.includes(item.file_type));
      const enriched = this.galleryAttachments.find(
        item => item.data_url === attachment.data_url
      );
      this.activeAttachment = enriched || normalize(attachment);
      this.showGallery = true;
    },
    onGalleryClose() {
      this.showGallery = false;
    },
    fileExtensition(filename) {
      return filename.split('.').last();
    },
    fileName(filename) {
      const name = filename.substring(filename.lastIndexOf('/') + 1);
      try {
        return decodeURIComponent(name);
      } catch {
        return name;
      }
    },
    formatFileSize(bytes) {
      return formatBytes(bytes, 0);
    },
    specialFileType(attachment) {
      const fileType = this.effectiveFileType(attachment);
      return (
        fileType === 'image' ||
        fileType === 'video' ||
        fileType === 'location' ||
        fileType === 'contact'
      );
    },
    onThumbnailError(id) {
      this.$set(this.failedThumbnails, id, true);
    },
    specialFileTypeClass() {
      if (this.isLarge) return 'bg-neutral-200 rounded-lg w-10 h-10 flex items-center justify-center overflow-hidden';

      return 'w-6 h-6 rounded-[4px] flex items-center justify-center overflow-hidden';
    },
  },
};
</script>
<style lang="scss" scoped>
.documentIcon {
  @apply absolute top-0 h-full;
  @apply ltr:right-0 ltr:pl-[7px] ltr:pr-[1px];
  @apply rtl:left-0 rtl:pr-[7px] rtl:pl-[1px];
}
</style>
